import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, CheckCircle2, Clock3, FileWarning, Radio, ReceiptText, UserCheck, X } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, Loading, Notice, PageHeader, StatusBadge } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { hasAnyRole, type DashboardData } from '../lib/types';

export function DashboardPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const studyDateFrom = params.get('studyDateFrom') ?? '';
  const studyDateTo = params.get('studyDateTo') ?? '';
  const qs = new URLSearchParams();
  if (studyDateFrom) qs.set('studyDateFrom', studyDateFrom);
  if (studyDateTo) qs.set('studyDateTo', studyDateTo);
  const suffix = qs.toString() ? `?${qs}` : '';
  const withDate = (path: string) => {
    if (!qs.toString()) return path;
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}${qs}`;
  };
  const setDate = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };
  const clearDates = () => setParams(new URLSearchParams());
  const query = useQuery({ queryKey: ['dashboard', studyDateFrom, studyDateTo], queryFn: () => api<DashboardData>(`/api/dashboard${suffix}`), refetchInterval: 60_000 });
  if (query.isLoading) return <Loading />;
  if (query.error || !query.data) return <Notice kind="error">No fue posible cargar el tablero operativo.</Notice>;
  const { metrics, recentStudies, byState, byOrigin, overdueByState } = query.data;
  const cards = [
    ['Estudios activos visibles', metrics.myPending, Clock3, withDate('/studies'), 'info'],
    ['Captura pendiente', metrics.technicalPending, Activity, withDate('/work/technical'), 'technical'],
    ['Fuera de SLA', metrics.overdue, AlertTriangle, withDate('/studies'), 'warning'],
    ['Errores operativos', metrics.errors, FileWarning, withDate('/studies?stateCode=ERROR_AUT'), 'danger'],
    ['Enviados hoy', metrics.sentToday, CheckCircle2, withDate('/studies?stateCode=ENVIADO'), 'success'],
    ['Pacientes por validar', metrics.pendingValidation, UserCheck, '/patients?dataStatus=PENDIENTE_VALIDACION', 'people'],
    ['Pendientes de facturación', metrics.pendingBilling, ReceiptText, '/work/billing', 'billing'],
    ['Sesiones en vivo hoy', metrics.liveSessionsToday, Radio, '/studies', 'live'],
  ] as const;
  const visibleCards = cards.filter(([label]) => label !== 'Pendientes de facturación' || hasAnyRole(user, ['FACTURACION', 'COORDINACION', 'ADMINISTRADOR', 'SUPERVISOR', 'AUDITOR']));
  return (
    <div className="dashboard-page">
      <PageHeader title="Panel operativo" description="Estado transversal de pacientes, estudios, entregas y puntos de control." />
      <Card className="filter-card">
        <div className="filter-grid">
          <label>
            Desde
            <input type="date" value={studyDateFrom} onChange={(event) => setDate('studyDateFrom', event.target.value)} />
          </label>
          <label>
            Hasta
            <input type="date" value={studyDateTo} onChange={(event) => setDate('studyDateTo', event.target.value)} />
          </label>
          {(studyDateFrom || studyDateTo) && <Button variant="ghost" onClick={clearDates}><X size={16} /> Limpiar</Button>}
        </div>
        <p className="filter-summary">{studyDateFrom || studyDateTo ? 'Panel filtrado por fecha del estudio.' : 'Panel sin filtro de fecha del estudio.'}</p>
      </Card>
      <div className="metric-grid dashboard-metrics">
        {visibleCards.map(([label, value, Icon, link, tone]) => <Link key={label} className={`metric-card metric-card-${tone}`} to={link}><Icon /><div><strong>{value}</strong><span>{label}</span></div></Link>)}
      </div>
      <div className="two-column dashboard-columns">
        <Card title="Estudios recientes" className="dashboard-main-card">
          <div className="table-wrap"><table><thead><tr><th>Estudio</th><th>Paciente</th><th>Origen</th><th>Estado</th><th>Responsable</th></tr></thead><tbody>
            {recentStudies.map((study) => <tr key={study.id}><td><Link to={`/studies/${study.id}`}>{study.code}</Link><small>{study.studyTypeName}</small></td><td>{study.patientName}<small><StatusBadge value={study.patient.dataStatus} /></small></td><td>{study.origin.replaceAll('_', ' ')}</td><td><StatusBadge value={study.stateCode} /></td><td>{study.currentResponsibleRole.replaceAll('_', ' ')}<small>{study.currentResponsibleEmail ?? 'Sin correo asignado'}</small></td></tr>)}
          </tbody></table></div>
        </Card>
        <div className="stack dashboard-side">
          <Card title="Fuera de SLA por etapa">
            {overdueByState.length ? (
              <div className="compact-list">
                {overdueByState.map((item) => (
                  <Link
                    className="compact-row"
                    key={`${item.stateCode}-${item.responsibleRole}`}
                    to={withDate(`/studies?stateCode=${item.stateCode}&responsibleRole=${item.responsibleRole}`)}
                  >
                    <span>{item.stateName}<small>{item.responsibleRole.replaceAll('_', ' ')}</small></span>
                    <strong>{item.count}</strong>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="muted">Sin estudios vencidos en el alcance visible.</p>
            )}
          </Card>
          <Card title="Distribución por estado"><div className="compact-list">{byState.map((item) => <div className="compact-row" key={item.stateCode}><span>{item.stateName}</span><strong>{item._count._all}</strong></div>)}</div></Card>
          <Card title="Distribución por origen"><div className="compact-list">{byOrigin.map((item) => <div className="compact-row" key={item.origin}><span>{item.origin.replaceAll('_', ' ')}</span><strong>{item._count._all}</strong></div>)}</div></Card>
        </div>
      </div>
    </div>
  );
}
